import { Rectangle } from "../primitives"
import { ChartImage } from "./chart-image"
import { Font } from "./font"
import { Gradient } from "./gradient"
import { Shadow } from "./shadow"
import { Stroke } from "./stroke"

/**
 * @public
 * @class Contains visual parameters like fill, shadow, image, font.
 * @param {Tee.Chart} chart The parent chart this format object belongs to.
 */
class Format {
	constructor(chart) {
		this.chart = chart

		this.gradient = new Gradient(chart)
		this.fill = "rgb(200,200,200)"
		this.stroke = new Stroke(chart)

		this.round = { x: 0, y: 0 }
		this.transparency = 0

		this.doSuperNums = false
		this.doSubNums = false

		this.font = new Font(chart)
		this.shadow = new Shadow(chart)

		this._img = null
		this._tmpPolygonBounds = new Rectangle()
	}

	get image() {
		if (!this._img) {
			this._img = new ChartImage(this.chart)
		}

		return this._img
	}

	/**
	 * Draws a rectangle with rounded corners.
	 */
	roundRect(ctx, x, y, width, height) {
		if (ctx.roundRect) {
			ctx.roundRect(x, y, width, height, this.round.x, this.round.y)
			return
		}

		let r = x + width
		let b = y + height
		let xr = this.round.x
		let yr = this.round.y
		const corners = this.round.corners

		if (height < 0) {
			y = b
			b = y - height
		}

		if (width < 0) {
			x = r
			r = x - width
		}

		if (2 * xr > width) xr = width * 0.5
		if (2 * yr > height) yr = height * 0.5

		!corners || corners[0] ? ctx.moveTo(x + xr, y) : ctx.moveTo(x, y)

		if (!corners || corners[1]) {
			ctx.lineTo(r - xr, y)
			ctx.quadraticCurveTo(r, y, r, y + yr)
		} else {
			ctx.lineTo(r, y)
		}

		if (!corners || corners[2]) {
			ctx.lineTo(r, b - yr)
			ctx.quadraticCurveTo(r, b, r - xr, b)
		} else {
			ctx.lineTo(r, b)
		}

		if (!corners || corners[3]) {
			ctx.lineTo(x + xr, b)
			ctx.quadraticCurveTo(x, b, x, b - yr)
		} else {
			ctx.lineTo(x, b)
		}

		if (!corners || corners[0]) {
			ctx.lineTo(x, y + yr)
			ctx.quadraticCurveTo(x, y, x + xr, y)
		} else {
			ctx.lineTo(x, y)
		}

		ctx.closePath()
	}

	textHeight() {
		return this.font.getSize() * 1.3
	}

	textWidth(text) {
		return this.chart.ctx.measureText(text).width
	}

	fillBack(c, getbounds, x, y, width, height) {
		if (this.gradient.visible) {
			c.fillStyle = getbounds ? this.gradient.create(getbounds()) : this.gradient.rect(x, y, width, height)
			c.fill()
		} else if (this.fill !== "") {
			c.fillStyle = this.fill
			c.fill()
		}
	}

	draw(c, getbounds, x, y, width, height) {
		const i = this._img
		let oldtransp

		if (typeof x === "object") {
			y = x.y
			width = x.width
			height = x.height
			x = x.x
		}

		if (this.transparency > 0) {
			oldtransp = c.globalAlpha
			c.globalAlpha = (1 - this.transparency) * oldtransp
		}

		this.shadow.prepare(c)

		if (i && i.visible && i.url !== "") {
			c.save()
			c.clip()

			if (i.backFill === true) {
				this.fillBack(c, getbounds, x, y, width, height)
			}

			if (getbounds) {
				const r = getbounds()
				i.tryDraw(r.x, r.y, r.width, r.height)
			} else {
				i.tryDraw(x, y, width, height)
			}

			c.restore()
		} else {
			this.fillBack(c, getbounds, x, y, width, height)
		}

		if (this.stroke.fill !== "") {
			this.stroke.prepare()
			c.stroke()
		}

		if (this.transparency > 0) {
			c.globalAlpha = oldtransp
		}
	}

	subNums(str) {
		let newStr = ""

		for (let i = 0; i < str.length; i++) {
			const code = str.charCodeAt(i)

			if (code >= 48 && code <= 57) {
				newStr += String.fromCharCode(code + 8272)
			} else {
				newStr += str[i]
			}
		}

		return newStr
	}

	superNums(str) {
		let newStr = ""

		for (let i = 0; i < str.length; i++) {
			const code = str.charCodeAt(i)

			if (code === 48 || (code >= 52 && code <= 57)) {
				newStr += String.fromCharCode(code + 8256)
			} else if (code === 49) {
				newStr += String.fromCharCode(185)
			} else if (code === 50) {
				newStr += String.fromCharCode(178)
			} else if (code === 51) {
				newStr += String.fromCharCode(179)
			} else {
				newStr += str[i]
			}
		}

		return newStr
	}

	drawText(bounds, text) {
		const g = this.font._g
		const c = this.chart.ctx
		const s = this.font._s
		const a = this.font.textAlign
		let x = bounds.x
		let y = bounds.y

		const drawAtXY = value => {
			c.fillText(value, x, y)

			if (s && s.fill !== "") {
				s.prepare()
				c.strokeText(value, x, y)
			}
		}

		if (this.font.lineThrough) {
			c.beginPath()
			c.moveTo(bounds.x, bounds.y + bounds.height * 0.4)
			c.lineTo(bounds.x + bounds.width, bounds.y + bounds.height * 0.4)
			c.stroke()
			c.closePath()
		}

		c.fillStyle = g && g.visible && bounds ? g.create(bounds) : this.font.fill

		if (a === "center") x += 0.5 * bounds.width
		else if (a === "right" || a === "end") x += bounds.width

		const rows = `${text}`.split("\n")
		const l = rows.length

		if (l > 1) {
			const h = this.textHeight(rows[0])

			for (let t = 0; t < l; t++) {
				if (this.doSuperNums) drawAtXY(this.superNums(rows[t]))
				else if (this.doSubNums) drawAtXY(this.subNums(rows[t]))
				else drawAtXY(rows[t])

				y += h
			}
		} else {
			if (this.doSuperNums) drawAtXY(this.superNums(text))
			else if (this.doSubNums) drawAtXY(this.subNums(text))
			else drawAtXY(text)
		}
	}

	rectangle(x, y, width, height) {
		if (this.transparency < 1) {
			if (typeof x === "object") {
				this.rectangle(x.x, x.y, x.width, x.height)
			} else {
				this.rectPath(x, y, width, height)
				this.draw(this.chart.ctx, null, x, y, width, height)
			}
		}
	}

	polygonBounds(points, r) {
		let x0 = 0
		let y0 = 0
		let x1 = 0
		let y1 = 0

		const l = points.length

		if (l > 0) {
			x0 = x1 = points[0].x
			y0 = y1 = points[0].y

			for (let t = 1; t < l; t++) {
				let p = points[t].x
				if (p < x0) x0 = p
				else if (p > x1) x1 = p

				p = points[t].y
				if (p < y0) y0 = p
				else if (p > y1) y1 = p
			}
		}

		r.x = x0
		r.y = y0
		r.width = x1 - x0
		r.height = y1 - y0
	}

	polygon(points) {
		const c = this.chart.ctx
		const l = points.length

		c.beginPath()
		c.moveTo(points[0].x, points[0].y)

		for (let t = 1; t < l; t++) {
			c.lineTo(points[t].x, points[t].y)
		}

		c.closePath()

		this.draw(c, () => {
			this.polygonBounds(points, this._tmpPolygonBounds)
			return this._tmpPolygonBounds
		})
	}

	ellipsePath(c, cx, cy, width, height) {
		if (this.chart.__webgl) {
			c.z = this.z
			c.depth = this.depth
			c.ellipsePath(cx, cy, width, height)
		} else {
			c.save()
			c.translate(cx, cy)
			c.scale(width * 0.5, height * 0.5)
			c.beginPath()
			c.arc(0, 0, 1, 0, 2 * Math.PI, false)
			c.restore()
		}
	}

	ellipse(cx, cy, width, height) {
		const c = this.chart.ctx
		this.ellipsePath(c, cx, cy, width, height)
		this.draw(c, null, cx - width * 0.5, cy - height * 0.5, width, height)
	}

	sphere(cx, cy, width, height) {
		if (this.chart.__webgl) {
			const ctx = this.chart.ctx
			ctx.depth = this.depth
			ctx.z = this.z

			if (this.gradient.visible) {
				ctx.fillStyle = this.gradient.colors[this.gradient.colors.length - 1]
			}

			ctx.sphere(cx, cy, width, height)
		} else {
			this.ellipse(cx, cy, width, height)
		}
	}

	cylinder(r, topradius, vertical, inverted) {
		if (this.chart.__webgl) {
			const ctx = this.chart.ctx
			ctx.depth = this.depth
			ctx.z = this.z
			ctx.image = this.image
			ctx.cylinder(r, topradius, vertical, inverted)
			return
		}

		if (topradius === 1) {
			this.cube(r)
		} else {
			const w = r.width
			const h = r.height

			if (vertical) {
				this.polygon([new Point(r.x + w * 0.5, r.y), new Point(r.x, r.y + h), new Point(r.x + w, r.y + h)])
			} else {
				this.polygon([new Point(r.x + w, r.y + h * 0.5), new Point(r.x, r.y), new Point(r.x, r.y + h)])
			}
		}
	}

	cube(r) {
		const a = this.chart.aspect
		const is3D = a.view3d
		let w
		let h
		let old
		let ax = 0
		let ay = 0

		if (is3D) {
			if (this.chart.__webgl) {
				const ctx = this.chart.ctx
				ctx.depth = this.depth
				ctx.z = this.z
				ctx.cube(r, this.round.x)
				return
			}

			const z = this.z
			const depth = this.depth

			ax = z * a._orthox
			ay = -z * a._orthoy

			w = r.x + r.width
			h = r.y + r.height

			const dx = depth * a._orthox
			const dy = -depth * a._orthoy

			old = this.shadow.visible
			this.shadow.visible = false

			const ww = w + dx
			const hh = r.y + dy

			if (depth > 0) {
				this.polygon([
					{ x: w, y: r.y },
					{ x: ww, y: hh },
					{ x: ww, y: h + dy },
					{ x: w, y: h }
				])

				if (r.width > 0) {
					this.polygon([
						{ x: r.x, y: r.y },
						{ x: r.x + dx, y: hh },
						{ x: ww, y: hh },
						{ x: w, y: r.y }
					])
				}
			}
		}

		this.rectPath(r.x + ax, r.y + ay, r.width, r.height)

		if (is3D) {
			this.shadow.visible = old
		}
	}

	rectPath(x, y, width, height) {
		const c = this.chart.ctx

		c.beginPath()

		if (this.round.x > 0 && this.round.y > 0) this.roundRect(c, x, y, width, height)
		else c.rect(x, y, width, height)
	}

	setChart(chart) {
		this.chart = chart
		this.shadow.chart = chart
		this.gradient.chart = chart
		this.font.setChart(chart)

		if (this._img) {
			this._img.chart = chart
		}

		this.stroke.setChart(chart)
	}
}

export { Format }

